import ButtonWithAnalytics from "@/features/Common/components/ButtonWithAnalytics";
import DirectToDoctorInPersonHelpModal from "@/features/MedicalConsultations/components/DirectToDoctorInPersonHelpModal";
import FullscreenBackground from "@/features/Common/components/FullscreenBackground";
import IconLoader from "@/features/Common/components/IconLoader";
import StepsCounter from "@/features/Common/components/StepsCounter";
import WatermarkIcon from "@/features/Common/components/WatermarkIcon";
import {
  trackEvent,
  trackPageViewV2,
  trackUserInteraction,
} from "@/services/analytics-adapter";
import theme from "@/theme";
import { convertPxToRem } from "@/utils";
import { Box, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import MedicalConsultationPartnerMoreInfoModal from "../MedicalConsultationPartnerMoreInfoModal";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";
import { MembershipDependent } from "@/services/core-api-adapter";
import SelectedDependantPanel from "@/features/Common/components/SelectedDependantPanel";

interface ConsultationPromptProps {
  onBackButtonClick: () => void;
  onContinueButtonClick: () => void;
  onFindADoctorButtonClick: () => void;
  selectedDependant?: MembershipDependent | null;
  consultationType?: "DirectToDoctor" | "NurseConsult";
}

export function ConsultationPrompt({
  onContinueButtonClick,
  onBackButtonClick,
  onFindADoctorButtonClick,
  selectedDependant,
  consultationType = "DirectToDoctor",
}: ConsultationPromptProps) {
  const { t } = useTranslation();

  const [isPersonHelpModalOpen, setIsPersonHelpModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const shouldShowDependantPanel =
    selectedDependant &&
    isFeatureEnabled(FeatureToggleIdentifier.ENABLE_DEPENDANT_SERVICE_OFFERING);

  function onContinue() {
    trackEvent({
      event: "action.nextStepsContinue",
      source: "Medical Assistance",
    });
    trackUserInteraction({
      linkText: "Continue | Medical assistance next steps",
      linkIntent: "navigational",
      linkScope: "button",
    });
    onContinueButtonClick();
  }

  function onInPersonHelpButtonClick() {
    setIsPersonHelpModalOpen(true);
  }

  useEffect(() => {
    trackPageViewV2({
      pageName: `Medical assistance:${consultationType} prompt`,
      pageSubSection1: "Medical assistance",
      pageSubSection2: `Medical assistance:${consultationType} prompt`,
      pageCategory: "Medical assistance",
    });
  }, []);

  function onInfoIconClick() {
    setIsInfoModalOpen(true);
  }

  function onMoreInfoModalClose() {
    setIsInfoModalOpen(false);
  }

  return (
    <>
      <Stack p={2} justifyContent="space-between" flexGrow={1} minHeight="100%">
        <Stack
          color="neutral.50"
          sx={{
            height: "100%",
          }}
        >
          <Stack spacing={3}>
            <Stack spacing={2}>
              <Stack
                direction="row"
                alignSelf="center"
                alignItems="center"
                spacing={2.5}
                p={2}
              >
                <WatermarkIcon
                  height={36}
                  width={36}
                  color={"neutral.50"}
                  foreground="neutral.50"
                  Icon={"FaceIconBackground"}
                  iconSize="large"
                />

                <IconLoader
                  icon="ArrowNextIcon"
                  sx={{
                    fontSize: convertPxToRem(25),
                    alignSelf: "center",
                    color: "neutral.50",
                  }}
                />
                <Stack spacing={1} direction="row" alignItems="center">
                  <Box
                    src={`/assets/MedicalAssistancePartners/Intercare/logo.svg`}
                    component="img"
                    sx={{
                      zIndex: 2,
                    }}
                    alt={t(
                      "CVPFlow.screens.HealthRecord.animationAlternateText"
                    )}
                  />

                  <ButtonWithAnalytics
                    page="Direct to doctor prompt"
                    text={t("MedicalConsultationPartner.more.info.modal.title")}
                    intent="informational"
                    variant="text"
                    aria-label={t(
                      "MedicalConsultationPartner.more.info.modal.title"
                    )}
                  >
                    <IconLoader
                      icon="InformationSymbolInCircleIcon"
                      color="info"
                      onClick={onInfoIconClick}
                      sx={{ fontSize: convertPxToRem(20), cursor: "pointer" }}
                    />
                  </ButtonWithAnalytics>
                </Stack>
              </Stack>
            </Stack>
            <Stack spacing={2}>
              {shouldShowDependantPanel && (
                <SelectedDependantPanel
                  name={
                    selectedDependant.firstName +
                    " " +
                    selectedDependant.lastName
                  }
                />
              )}

              <Stack
                bgcolor="neutral.50"
                borderRadius={theme.shape.borderRadius}
                p={4}
                spacing={2}
              >
                <Typography textAlign="left" variant="h2">
                  {t(`${consultationType}.prompt.panel.title`)}
                </Typography>

                <Stack direction="row">
                  <Stack>
                    <StepsCounter stepCounter={1} />
                  </Stack>
                  <Stack>
                    <Typography ml={2} variant="body1">
                      <Trans
                        i18nKey={`${consultationType}.prompt.panel.step1Label`}
                      ></Trans>
                    </Typography>
                  </Stack>
                </Stack>

                <Stack direction="row">
                  <Stack>
                    <StepsCounter stepCounter={2} />
                  </Stack>

                  <Stack>
                    <Stack>
                      <Typography ml={2} variant="body1">
                        <Trans
                          i18nKey={`${consultationType}.prompt.panel.step2Label`}
                        ></Trans>
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>

                <Stack direction="row">
                  <Stack>
                    <StepsCounter stepCounter={3} />
                  </Stack>
                  <Stack>
                    <Typography ml={2} variant="body1">
                      <Trans
                        i18nKey={`${consultationType}.prompt.panel.step3Label`}
                      ></Trans>
                    </Typography>
                    <Typography ml={2} variant="body2">
                      <Trans
                        i18nKey={`${consultationType}.prompt.panel.step3Label.subtext`}
                      ></Trans>
                    </Typography>
                  </Stack>
                </Stack>

                <Stack direction="row">
                  <Stack>
                    <StepsCounter stepCounter={4} />
                  </Stack>
                  <Stack>
                    <Typography ml={2} variant="body1">
                      <Trans
                        i18nKey={`${consultationType}.prompt.panel.step4Label`}
                      ></Trans>
                    </Typography>
                    <Typography ml={2} variant="body2">
                      <Trans
                        i18nKey={`${consultationType}.prompt.panel.step4Label.subtext`}
                      ></Trans>
                    </Typography>
                  </Stack>
                </Stack>
                <ButtonWithAnalytics
                  page="DirectToDoctorPrompt"
                  text={t(`${consultationType}.prompt.panel.inPersonHelp.CTA`)}
                  intent="navigational"
                  variant="text"
                  onClick={onInPersonHelpButtonClick}
                >
                  {t(`${consultationType}.prompt.panel.inPersonHelp.CTA`)}
                </ButtonWithAnalytics>
              </Stack>
            </Stack>

            <Typography textAlign="center" color="info">
              {t(`${consultationType}.prompt.footer.title`)}
            </Typography>

            <Stack direction="row" spacing={2}>
              <ButtonWithAnalytics
                page="DirectToDoctorPrompt"
                text={t("common.backButton")}
                intent="navigational"
                onClick={onBackButtonClick}
                color="info"
                fullWidth
                variant="outlined"
              >
                {t("common.backButton")}
              </ButtonWithAnalytics>
              <ButtonWithAnalytics
                page="DirectToDoctorPrompt"
                text={t("common.continueButton")}
                intent="navigational"
                onClick={onContinue}
                color="info"
                fullWidth
              >
                {t("common.continueButton")}
              </ButtonWithAnalytics>
            </Stack>
          </Stack>
        </Stack>
        <FullscreenBackground color="primary.main" />
      </Stack>
      <MedicalConsultationPartnerMoreInfoModal
        isOpen={isInfoModalOpen}
        onClose={onMoreInfoModalClose}
      />
      <DirectToDoctorInPersonHelpModal
        isOpen={isPersonHelpModalOpen}
        onFindADoctorButtonClick={onFindADoctorButtonClick}
        onClose={() => setIsPersonHelpModalOpen(false)}
      />
    </>
  );
}
